import React, { useState, useEffect } from 'react';
import {
  Form, Input, Button, Upload, Row, Col, InputNumber, Switch, Radio, Space, Typography, message
} from 'antd';
import {
  UploadOutlined, SaveOutlined, CloseOutlined, InboxOutlined, FilePdfOutlined,
  FileImageOutlined, TagsOutlined, LinkOutlined, SettingOutlined
} from '@ant-design/icons';
import { createBlogHighlight, updateBlogHighlight } from '../../../api/blogHighlightApi';

const { Text } = Typography;

const BlogHighlightForm = ({ blogHighlight, onSubmit, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [pdfFileList, setPdfFileList] = useState([]);
  const [linkType, setLinkType] = useState('pdf');

  useEffect(() => {
    if (blogHighlight) {
      if (blogHighlight.image) {
        setFileList([{
          uid: '-1',
          name: 'Current Image',
          status: 'done',
          url: blogHighlight.image
        }]);
      }

      if (blogHighlight.linkType === 'pdf' && blogHighlight.link) {
        setPdfFileList([{
          uid: '-2',
          name: blogHighlight.link.split('/').pop(),
          status: 'done',
          url: blogHighlight.link
        }]);
      }

      setLinkType(blogHighlight.linkType || 'pdf');

      form.setFieldsValue({
        title: blogHighlight.title || '',
        linkType: blogHighlight.linkType || 'pdf',
        link: blogHighlight.linkType !== 'pdf' ? blogHighlight.link : '',
        badgeDay: blogHighlight.badgeDay || '',
        badgeMonth: blogHighlight.badgeMonth || '',
        badgeYear: blogHighlight.badgeYear || '',
        order: blogHighlight.order || 0,
        isActive: blogHighlight.isActive !== undefined ? blogHighlight.isActive : true,
      });
    } else {
      form.resetFields();
      setFileList([]);
      setPdfFileList([]);
      setLinkType('pdf');
    }
  }, [blogHighlight, form]);

  const customRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handlePdfUploadChange = ({ fileList: newFileList }) => {
    setPdfFileList(newFileList);
  };

  const onFinish = async (values) => {
    if (values.linkType === 'pdf' && pdfFileList.length === 0) {
      message.error('Please upload a PDF file');
      return;
    }
    if (values.linkType !== 'pdf' && !values.link) {
      message.error('Please enter a link');
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();

      submitData.append('title', values.title);
      submitData.append('linkType', values.linkType);
      submitData.append('link', values.linkType !== 'pdf' ? values.link : '');
      submitData.append('badgeDay', values.badgeDay || '');
      submitData.append('badgeMonth', values.badgeMonth || '');
      submitData.append('badgeYear', values.badgeYear || '');
      submitData.append('order', values.order || 0);
      submitData.append('isActive', values.isActive);

      if (fileList.length > 0 && fileList[0].originFileObj) {
        submitData.append('image', fileList[0].originFileObj);
      } else if (blogHighlight?.image) {
        submitData.append('imageUrl', blogHighlight.image);
      }

      if (values.linkType === 'pdf' && pdfFileList.length > 0 && pdfFileList[0].originFileObj) {
        submitData.append('pdf', pdfFileList[0].originFileObj);
      } else if (values.linkType === 'pdf' && blogHighlight?.link) {
        submitData.append('link', blogHighlight.link);
      }

      if (blogHighlight) {
        await updateBlogHighlight(blogHighlight._id, submitData);
        message.success('Blog highlight updated successfully');
      } else {
        await createBlogHighlight(submitData);
        message.success('Blog highlight created successfully');
      }

      form.resetFields();
      setFileList([]);
      setPdfFileList([]);
      onSubmit();
    } catch (error) {
      console.error('Error saving blog highlight:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save blog highlight';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formStyle = {
    fontFamily: 'Rubik, sans-serif',
    fontSize: '16px',
    fontWeight: 400,
    color: '#000000'
  };

  const sectionStyle = {
    background: '#fafafa',
    border: '1px solid #f0f0f0',
    borderRadius: 8,
    padding: 16,
    marginBottom: 20
  };

  const sectionHeaderStyle = { marginBottom: 16 };

  return (
    <div style={{ fontFamily: 'Rubik, sans-serif' }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          isActive: true,
          order: 0,
          linkType: 'pdf'
        }}
        style={formStyle}
      >
        {/* Basic Details */}
        <div style={sectionStyle}>
          <Space align="center" style={sectionHeaderStyle}>
            <FileImageOutlined style={{ color: '#470172' }} />
            <Text strong>Basic Details</Text>
          </Space>

          <Form.Item
            label={<Text type="secondary" style={{ fontSize: 13 }}>Title</Text>}
            name="title"
            rules={[
              { required: true, message: 'Please enter the title' },
              { max: 200, message: 'Title cannot exceed 200 characters' }
            ]}
            style={{ marginBottom: 16 }}
          >
            <Input placeholder="Enter highlight title" style={formStyle} />
          </Form.Item>

          <Form.Item
            label={<Text type="secondary" style={{ fontSize: 13 }}>Background Image</Text>}
            rules={[{ required: !blogHighlight, message: 'Please upload an image' }]}
            style={{ marginBottom: 0 }}
          >
            <Upload
              listType="picture-card"
              fileList={fileList}
              onChange={handleUploadChange}
              customRequest={customRequest}
              beforeUpload={(file) => {
                const isImage = file.type.startsWith('image/');
                if (!isImage) {
                  message.error('You can only upload image files!');
                }
                const isLt5M = file.size / 1024 / 1024 < 5;
                if (!isLt5M) {
                  message.error('Image must be smaller than 5MB!');
                }
                return isImage && isLt5M;
              }}
              maxCount={1}
            >
              {fileList.length === 0 && (
                <div>
                  <InboxOutlined style={{ fontSize: 40, color: '#470172' }} />
                  <div style={{ marginTop: 8, ...formStyle }}>Upload Image</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </div>

        {/* Badge */}
        <div style={sectionStyle}>
          <Space align="center" style={sectionHeaderStyle}>
            <TagsOutlined style={{ color: '#470172' }} />
            <Text strong>Badge</Text>
          </Space>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label={<Text type="secondary" style={{ fontSize: 13 }}>Badge Number</Text>}
                name="badgeDay"
                tooltip="Large number shown on the badge, e.g. 1, 2, 3"
                style={{ marginBottom: 0 }}
              >
                <Input placeholder="e.g. 1" style={formStyle} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label={<Text type="secondary" style={{ fontSize: 13 }}>Badge Label</Text>}
                name="badgeMonth"
                tooltip="Small label shown on the badge, e.g. Edition"
                style={{ marginBottom: 0 }}
              >
                <Input placeholder="e.g. Edition" style={formStyle} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label={<Text type="secondary" style={{ fontSize: 13 }}>Badge Year</Text>}
                name="badgeYear"
                style={{ marginBottom: 0 }}
              >
                <Input placeholder="e.g. 2026" style={formStyle} />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Link */}
        <div style={sectionStyle}>
          <Space align="center" style={sectionHeaderStyle}>
            <LinkOutlined style={{ color: '#470172' }} />
            <Text strong>Link</Text>
          </Space>

          <Form.Item
            label={<Text type="secondary" style={{ fontSize: 13 }}>Link Type</Text>}
            name="linkType"
            style={{ marginBottom: linkType === 'pdf' ? 8 : 16 }}
          >
            <Radio.Group
              onChange={(e) => setLinkType(e.target.value)}
              style={formStyle}
            >
              <Radio value="pdf">PDF Document</Radio>
              <Radio value="external">External URL</Radio>
              {/* <Radio value="internal">Internal Page</Radio> */}
            </Radio.Group>
          </Form.Item>

          {linkType === 'pdf' && (
            <Form.Item label={<Text type="secondary" style={{ fontSize: 13 }}>PDF File</Text>} style={{ marginBottom: 0 }}>
              <Upload
                fileList={pdfFileList}
                onChange={handlePdfUploadChange}
                customRequest={customRequest}
                beforeUpload={(file) => {
                  const isPdf = file.type === 'application/pdf';
                  if (!isPdf) {
                    message.error('You can only upload PDF files!');
                  }
                  const isLt20M = file.size / 1024 / 1024 < 20;
                  if (!isLt20M) {
                    message.error('PDF must be smaller than 20MB!');
                  }
                  return isPdf && isLt20M;
                }}
                maxCount={1}
                icon={<FilePdfOutlined />}
              >
                <Button icon={<UploadOutlined />} style={formStyle}>Upload PDF</Button>
              </Upload>
            </Form.Item>
          )}

          {linkType !== 'pdf' && (
            <Form.Item
              label={<Text type="secondary" style={{ fontSize: 13 }}>{linkType === 'external' ? 'External URL' : 'Internal Page Path'}</Text>}
              name="link"
              rules={[{ required: true, message: 'Please enter a link' }]}
              style={{ marginBottom: 0 }}
            >
              <Input
                placeholder={linkType === 'external' ? 'https://example.com/article' : '/schemes/xyz'}
                style={formStyle}
              />
            </Form.Item>
          )}
        </div>

        {/* Display Settings */}
        <div style={{ ...sectionStyle, marginBottom: 8 }}>
          <Space align="center" style={sectionHeaderStyle}>
            <SettingOutlined style={{ color: '#470172' }} />
            <Text strong>Display Settings</Text>
          </Space>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item
                label={<Text type="secondary" style={{ fontSize: 13 }}>Order</Text>}
                name="order"
                tooltip="Display order (lower numbers appear first)"
                style={{ marginBottom: 0 }}
              >
                <InputNumber min={0} style={{ width: '100%', ...formStyle }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                label={<Text type="secondary" style={{ fontSize: 13 }}>Active</Text>}
                name="isActive"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Action Buttons */}
        <Row justify="end" gutter={8}>
          <Col>
            <Button onClick={onCancel} icon={<CloseOutlined />} style={formStyle}>
              Cancel
            </Button>
          </Col>
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
              style={{
                ...formStyle,
                backgroundColor: '#470172',
                borderColor: '#470172',
                color: '#ffffff'
              }}
            >
              {blogHighlight ? 'Update' : 'Create'}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default BlogHighlightForm;
